import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileText, Book, Download, ExternalLink, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'All Documents' },
  { id: 'user-manual', name: 'User Manuals' },
  { id: 'recipe-book', name: 'Recipe Books' },
  { id: 'quick-start', name: 'Quick Start Guides' },
  { id: 'maintenance', name: 'Maintenance Guides' },
  { id: 'other', name: 'Other' }
];

const getCategoryIcon = (category) => {
  switch (category) {
    case 'user-manual': return FileText;
    case 'recipe-book': return Book;
    case 'quick-start': return FileText;
    case 'maintenance': return FileText;
    default: return FileText;
  }
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, [selectedCategory]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const res = await axios.get(`${API_URL}/api/documents${params}`);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.description && doc.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getDocumentUrl = (doc) => {
    if (doc.external_url) return doc.external_url;
    if (doc.file_url) return `${API_URL}${doc.file_url}`;
    return '#';
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Documents & Resources</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Find user manuals, recipe books, quick start guides, and more for your HomeLyph freeze dryer.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? 'bg-[#003456]' : ''}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Documents Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-[#003456] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Documents Found</h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Documents will appear here once they are added by the admin.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDocuments.map((doc) => {
                const Icon = getCategoryIcon(doc.category);
                const url = getDocumentUrl(doc);
                const isExternal = !!doc.external_url;
                
                return (
                  <div key={doc.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-[#003456]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#0a0203] mb-1 truncate">{doc.title}</h3>
                        <p className="text-gray-500 text-sm mb-3 line-clamp-2">{doc.description || 'No description'}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-[#f0f4f8] rounded-full text-gray-600 capitalize">
                            {doc.category.replace('-', ' ')}
                          </span>
                          <span className="text-xs px-2 py-1 bg-[#f0f4f8] rounded-full text-gray-600 uppercase">
                            {doc.file_type}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <a 
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-[#003456] hover:bg-[#001d30] text-white rounded-lg transition-colors"
                      >
                        {isExternal ? (
                          <>
                            <ExternalLink className="h-4 w-4" />
                            Open Link
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            Download
                          </>
                        )}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Help Section */}
          <div className="mt-16 bg-[#003456] rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Can't Find What You're Looking For?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              If you need a specific document or have questions about your HomeLyph freeze dryer, 
              our support team is here to help.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/customer-support">
                <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                  Contact Support
                </Button>
              </Link>
              <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                  Customer Portal
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Documents;
